import { writable } from 'svelte/store'

import axios from 'axios'

import dialogStore from '../components/dialog/store.js'


// How long before a token expires should we consider it expired.
const TOKEN_EXPIRY_BUFFER_SECS = 30
const MAINTENANCE_INTERVAL_SECS = 60
const REQUEST_TIMEOUT_SECS = 5

const LOGIN_ENDPOINT = '/login'

class LoginRequired extends Error {
  constructor(...params) {
    super(...params)

    // For V8
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, LoginRequired)
    }

    this.name = 'LoginRequired'
  }
}

const redirectToLogin = function (continueUrl) {
  const url_params = {
    continue: continueUrl,
  }

  const url = LOGIN_ENDPOINT + '?' + Object.keys(url_params).map((key) => {
    return key + '=' + encodeURIComponent(url_params[key])
  }).join('&')
  location.href = url
}

const fetchAuth = function () {
  const requestDt = new Date()

  // Will fail if the user doesn't have a session.
  return axios.post('/api/auth')
    .then(response => {
      const userId = response.data.user_id
      const token = response.data.token
      const expiresInMs = (response.data.expires_in - TOKEN_EXPIRY_BUFFER_SECS) * 1000
      const expiresAt = new Date(requestDt.getTime() + expiresInMs)
      return {userId, token, expiresAt}
    })
    .catch(error => {
      if (error.response && error.response.status === 403) {
        throw new LoginRequired('No user session')
      } else {
        throw error
      }
    })
}

// Create an auth object.
//
// The auth object is a svelte store containing the last known auth data.
//
// The initial store value is undefined - it's fetched as needed.
// After auth data has been fetched once, the stored auth data will _not_ be reset if the user needs
// to log in again - i.e. UI elements depending on this store won't be updated.
// The user will be informed via a dialog.
//
// Methods are also provided to work with auth in other ways.
const createStore = function () {
  let authData

  // Set up the store.
  // It will be initialised when auth is first required, or when maintainAuth() is first run,
  // whichever is earliest.
  const { subscribe, set } = writable()

  // Update auth data.
  // Throws LoginRequired errors if no user is logged in, or the user has changed.
  const updateAuth = function () {
    return fetchAuth()
      .then(newAuthData => {
        if (authData && newAuthData.userId !== authData.userId) {
          throw new LoginRequired('Auth user changed')
        } else {
          authData = newAuthData
          set(authData)
          return authData
        }
      })
  }

  // Return auth data, fetching new data if none exists, or it has expired.
  const getAuth = function () {
    if (!authData || new Date() >= authData.expiresAt) {
      return updateAuth()
    } else {
      return Promise.resolve(authData)
    }
  }

  let authMaintenanceIntervalId

  const showLoginRequiredDialog = function () {
    // Once this dialog is shown there's no way for the user to close it and continue with their
    // existing session, so stop auth maintenance.
    if (authMaintenanceIntervalId !== undefined) {
      window.clearInterval(authMaintenanceIntervalId)
    }
    dialogStore.show('Login Required',
                     'Your session has expired. Please log in again to continue.',
                     [{name: 'Login', action: () => redirectToLogin(location.href)}])
  }

  // Maintain the auth data, ensuring it is updated when it expires. If the auth data can't be
  // updated, show a login required dialog to the user.
  const maintainAuth = function () {
    getAuth()
      .catch(error => {
        // Ignore other types of errors. If there's something wrong other than the user needing to
        // log in, e.g. the network is down, it may resolve itself before up to date auth data
        // is actually required.
        if (error instanceof LoginRequired) {
          showLoginRequiredDialog()
        }
      })
  }
  // Run auth maintenance periodically.
  authMaintenanceIntervalId = window.setInterval(maintainAuth, MAINTENANCE_INTERVAL_SECS * 1000)

  return {
    subscribe,

    // Run a function if the user is logged in.
    // Will redirect to login if the user isn't logged in.
    requireAuth: function (func) {
      return Promise.resolve()
        .then(getAuth)
        .then(func, error => {
          // Always redirect, even if the error isn't LoginRequired. If we can't ensure the user is
          // logged in, e.g. the network is down, it's safer to redirect to login.
          redirectToLogin(location.href)
        })
    },

    // Make a XHR request with authentication added.
    // Will show a login required dialog if the user isn't logged in.
    request: function (config) {
      return Promise.resolve()
        .then(getAuth)
        .catch(error => {
          if (error instanceof LoginRequired) {
            showLoginRequiredDialog()
          }
          // Throw error since the request failed. Prevents later then() calls from firing before
          // the browser actually performs the redirect.
          throw error
        })
        .then(authData => {
          if (config.hasOwnProperty('headers')) {
            config.headers['Authorization'] = 'Bearer ' + authData.token
          } else {
            config.headers = {'Authorization': 'Bearer ' + authData.token}
          }
          if (!config.hasOwnProperty('timeout')) {
            config.timeout = REQUEST_TIMEOUT_SECS * 1000
          }
          return axios.request(config)
        })
    },
  }
}

const authStore = createStore()

export default authStore
